/**
 * Reine Anzeige-Berechnung der Stempeluhr (TICKET P3-A2). Aus dem Status
 * (Zustand, Anker-Zeitstempel, bereits erfasste Netto-/Pausenzeit) und der
 * aktuellen Zeit werden die live tickenden Dauern abgeleitet — clientseitig,
 * ohne Sekundentakt-Polling gegen die API. Ändert nichts an der
 * Tagesberechnung (die bleibt in `@werkbank/timekeeping`).
 */
import { pflichtpauseLevel } from '@werkbank/timekeeping';

export type ClockState = 'OUT' | 'PRESENT' | 'BREAK';

export interface ClockStatusInput {
  state: ClockState;
  /** Start des laufenden Abschnitts (Arbeitsintervall bzw. Pause). */
  runningSince: string | null;
  /** Abgeschlossene Nettoarbeit heute (ohne das laufende Arbeitsintervall). */
  workedMinutes: number;
  /** Abgeschlossene Pausen heute (ohne die laufende Pause). */
  breakMinutes: number;
  /** Abgeschlossene anrechenbare Pausen (Abschnitte ≥ 15 min). */
  creditableBreakMinutes: number;
}

export interface ClockDisplay {
  /** Live-Nettoarbeitszeit heute (läuft nur im Zustand PRESENT). */
  netMinutes: number;
  /** Dauer der laufenden Pause (nur im Zustand BREAK, sonst 0). */
  currentBreakMinutes: number;
  /** Gesamte Pausenzeit heute inkl. laufender Pause. */
  breakTotalMinutes: number;
  /** Pflichtpausen-Vorwarnung (nur PRESENT). */
  hint: { level: 'amber' | 'red'; text: string } | null;
}

/** Ganze Minuten seit `iso` bis `nowMs` (nie negativ). */
export function minutesSince(iso: string | null, nowMs: number): number {
  if (!iso) return 0;
  return Math.max(0, Math.floor((nowMs - Date.parse(iso)) / 60000));
}

export function computeClockDisplay(s: ClockStatusInput, nowMs: number): ClockDisplay {
  const running = minutesSince(s.runningSince, nowMs);
  // Im Pausenzustand steht die Arbeitszeit still; es tickt allein die Pause.
  const netMinutes = s.workedMinutes + (s.state === 'PRESENT' ? running : 0);
  const currentBreakMinutes = s.state === 'BREAK' ? running : 0;
  const breakTotalMinutes = s.breakMinutes + currentBreakMinutes;

  // Vollanalyse S2 (Verhaltens-KORREKTUR): Die frühere lokale amber-Regel
  // (net >= 360 && cred < 15) widersprach dem kanonischen Paket — sie übersah
  // z. B. net=400/cred=20 (Pflicht wären 30 min) und feuerte am Grenzfall
  // exakt 6:00 h zu früh. Kanonisch gilt `pflichtpauseLevel`:
  // mandatoryBreak(net) (> 360 ⇒ 30 min, > 540 ⇒ 45 min) und amber/rot,
  // solange die anrechenbare Pause die Pflicht nicht deckt.
  let hint: ClockDisplay['hint'] = null;
  if (s.state === 'PRESENT') {
    const level = pflichtpauseLevel(netMinutes, s.creditableBreakMinutes);
    if (level !== 'none') hint = { level, text: 'Pflichtpause fällig' };
  }
  return { netMinutes, currentBreakMinutes, breakTotalMinutes, hint };
}

/**
 * Vollanalyse S2: „H:MM" kommt kanonisch aus lib/format.formatHm (identische
 * Implementierung); hier bleibt nur der Alias für die bestehenden Importe.
 */
export { formatHm as hmDuration } from './format';
